import { useSelector } from 'react-redux'
import { useIntl, defineMessages } from 'react-intl'
import { BuyerInfo } from '../../../../types/buyerInfo'
import { Link as ReactRouterLink } from 'react-router-dom'
import { formatCurrency } from '../../../../helpers/formatters'
import { useGetClientId } from '../../../../hooks/useGetClientId'
import { SEARCH_PATH_HELPERS } from '../../../../constants/navigation'
import { selectBuyerInfo } from '../../../../store/selectors/buyerInfo'
import { Box, Card, Icon, Flex, Text, LinkButton } from '@homebotapp/hb-react-component-catalog'

export const MSG = defineMessages({
  noBudget: {
    id: 'Affordability.noBudget',
    defaultMessage: 'Set a budget to get personalized affordability insights'
  },
  withinBudget: {
    id: 'Affordability.withinBudget',
    defaultMessage: 'This home is within your budget of {budget}'
  },
  notWithinBudget: {
    id: 'Affordability.notWithinBudget',
    defaultMessage: 'This home is not within your budget of {budget}'
  },
  explore: {
    id: 'Affordability.explore',
    defaultMessage: 'Adjust your budget'
  }
})

export interface AffordabilityProps {
  price: number
}

export const Affordability = ({ price }: AffordabilityProps) => {
  const intl = useIntl()
  const clientId = useGetClientId()
  const buyerInfo: BuyerInfo = useSelector(selectBuyerInfo)

  return (
    <Card mt={8} p={4} size='lg'>
      <Flex gap={4} direction={['column', null, 'row']} alignItems='center' justifyContent='space-between'>
        <Flex gap={4} alignItems='center'>
          <Box
            p={2}
            display='flex'
            borderRadius='50%'
            alignItems='center'
            justifyContent='center'
            bg={
              !buyerInfo?.affordability
                ? 'primary.100'
                : price <= buyerInfo.affordability
                ? 'success.100'
                : 'danger.100'
            }
          >
            <Icon
              h={6}
              w={6}
              name={
                !buyerInfo?.affordability
                  ? 'dollar-circle'
                  : price <= buyerInfo.affordability
                  ? 'check-circle-fa'
                  : 'times-circle'
              }
              color={
                !buyerInfo?.affordability
                  ? 'primary.700'
                  : price <= buyerInfo.affordability
                  ? 'success.700'
                  : 'danger.700'
              }
            />
          </Box>
          <Text as='span' size='sm' fontWeight='semibold' color='neutral.900'>
            {!buyerInfo?.affordability
              ? intl.formatMessage(MSG.noBudget)
              : price <= buyerInfo.affordability
              ? intl.formatMessage(MSG.withinBudget, { budget: formatCurrency(buyerInfo.affordability) })
              : intl.formatMessage(MSG.notWithinBudget, { budget: formatCurrency(buyerInfo.affordability) })}
          </Text>
        </Flex>
        <LinkButton
          size='sm'
          as={ReactRouterLink}
          width={['100%', null, 'auto']}
          rightIcon={<Icon name='circle-arrow-right-bts' h={3} w={3} />}
          to={SEARCH_PATH_HELPERS.affordabilityCalculator.buildPath(clientId)}
          tracking={{
            guid: 'xgm9rMNaoCf9kXonsh2p4s',
            ui_context: 'Listing Details Page - Affordability CTA',
            descriptor: MSG.explore.defaultMessage
          }}
        >
          {intl.formatMessage(MSG.explore)}
        </LinkButton>
      </Flex>
    </Card>
  )
}
